import { useQuery } from '@tanstack/react-query';
import { createPublicClient, http, Address, erc20Abi, Hash } from 'viem';
import { sepolia, bscTestnet } from 'viem/chains';
import { App_ABI } from '@packet/abi/App';
import { multicall3Abi } from '@packet/abi/multicall3';
import AppTokenWhitelist, { AppTokenWhitelistItem, NativeAddress } from '@packet/constants/AppTokenWhitelist';
import { base, bsc, mainnet, SupportedChain, SupportedChainId, SupportedChainMap, SupportedChains } from '@packet/constants/AppSupportChains';

// 从环境变量获取合约地址
const CONTRACT_ADDRESSES: Partial<Record<SupportedChainId, Address>> = {
  [sepolia.id]: import.meta.env.VITE_ETH_SEPOLIA_APP_CONTRACT as Address,
  [bscTestnet.id]: import.meta.env.VITE_BSC_TESTNET_APP_CONTRACT as Address,
  [mainnet.id]: import.meta.env.VITE_ETH_APP_CONTRACT as Address,
  [bsc.id]: import.meta.env.VITE_BSC_APP_CONTRACT as Address,
  [base.id]: import.meta.env.VITE_BASE_APP_CONTRACT as Address,
};

// 创建公共客户端
const getPublicClient = (chainId: SupportedChainId) => {
  const chain = SupportedChainMap[chainId];
  return createPublicClient({
    chain,
    transport: http(),
  });
};

export interface ChainElementsData {
  chainId: number;
  chainName: string;
  totalElements: bigint;
  contractAddress: Address;
}

export interface PoolInfo {
  token: Address;
  depositFeeBPS: number;
  withdrawFeeBPS: number;
  // relayFee: bigint;
  accumulatedFee: bigint;
  // feeRecipient: Address;
  viewerPK: readonly [bigint, bigint];
  freezerPK: readonly [bigint, bigint];
}

// Pool 数据和 Asset 信息的组合类型
export interface PoolWithAsset extends PoolInfo {
  poolId: bigint;
  chain: SupportedChain;
  symbol: string;
  name: string;
  decimals: number;
}

export interface TokenInfo extends PoolWithAsset {
  accumulatedFee: bigint;
  contractBalance: bigint;
}

// 工具函数：创建新数组
const NewArray = (length: number) => Array.from({ length }, (_, i) => i);

// 获取totalElements（分链展示）
export const useTotalElementsByChain = () => {
  return useQuery({
    queryKey: ['totalElementsByChain'],
    queryFn: async () => {
      const chainData: ChainElementsData[] = [];

      for (const chain of SupportedChains) {
        const client = getPublicClient(chain.id);
        const contractAddress = CONTRACT_ADDRESSES[chain.id];
        if (!contractAddress) {
          console.warn(`No contract address for chain ${chain.id}`);
          continue;
        }

        try {
          const totalElements = await client.readContract({ address: contractAddress, abi: App_ABI, functionName: 'totalElements' });
          chainData.push({ chainId: chain.id, chainName: chain.name, totalElements, contractAddress });
        } catch (error) {
          console.error(`Failed to get totalElements for chain ${chain.id}:`, error);
          chainData.push({ chainId: chain.id, chainName: chain.name, totalElements: 0n, contractAddress });
        }
      }

      return chainData;
    },
    refetchInterval: 30000, // 30秒刷新一次
  });
};

// 获取总的totalElements
export const useTotalElements = () => {
  const { data: chainData } = useTotalElementsByChain();

  return {
    data: chainData?.reduce((total, chain) => total + chain.totalElements, 0n) ?? 0n,
    chainData: chainData ?? [],
  };
};

// 通用的底层函数：获取所有链的 pool 数据
export const getAllPoolsData = async (): Promise<PoolWithAsset[]> => {
  const allPools: PoolWithAsset[] = [];

  for (const chain of SupportedChains) {
    const client = getPublicClient(chain.id);
    const contractAddress = CONTRACT_ADDRESSES[chain.id];

    if (!contractAddress) {
      console.warn(`No contract address for chain ${chain.id}`);
      continue;
    }

    try {
      // 获取 poolIds（这些就是 asset hash）
      const poolIdsReq = NewArray(10).map((index) => {
        return {
          address: contractAddress,
          abi: App_ABI,
          functionName: 'poolIds',
          args: [BigInt(index)],
        } as const;
      });

      const poolIds = await client.multicall({ contracts: poolIdsReq, allowFailure: true });

      // 获取有效的 poolId 和对应的 pool info
      const validPoolData = poolIds
        .map((res, index) => {
          if (res.status !== 'success' || res.result === 0n) return null;
          return { poolId: res.result, contractIndex: index };
        })
        .filter((v): v is NonNullable<typeof v> => !!v);

      if (validPoolData.length === 0) continue;

      // 获取 pool 信息
      const poolInfoContracts = validPoolData.map(({ poolId }) => {
        return {
          address: contractAddress,
          abi: App_ABI,
          functionName: 'getPoolInfo',
          args: [poolId],
        } as const;
      });

      const poolsQuery = await client.multicall({ contracts: poolInfoContracts, allowFailure: true });

      // 准备获取 token 信息的请求
      const poolsWithTokenInfo: Array<{ poolInfo: PoolInfo; poolId: bigint; tokenAddress: Address }> = [];

      validPoolData.forEach(({ poolId }, index) => {
        const poolResult = poolsQuery[index];
        if (poolResult.status !== 'success') return;

        const poolInfo = poolResult.result;
        poolsWithTokenInfo.push({ poolInfo, poolId, tokenAddress: poolInfo.token });
      });

      if (poolsWithTokenInfo.length === 0) continue;

      // 获取所有 token 的信息（symbol, name, decimals）
      const tokenInfoContracts = poolsWithTokenInfo.flatMap(({ tokenAddress }) => {
        if (tokenAddress === NativeAddress) {
          return []; // Native token 信息单独处理
        }
        return [
          { address: tokenAddress, abi: erc20Abi, functionName: 'symbol' },
          { address: tokenAddress, abi: erc20Abi, functionName: 'name' },
          { address: tokenAddress, abi: erc20Abi, functionName: 'decimals' },
        ] as const;
      });

      const tokenInfoResults = tokenInfoContracts.length > 0 ? await client.multicall({ contracts: tokenInfoContracts, allowFailure: true }) : [];

      // 组合数据
      let tokenInfoIndex = 0;
      for (const { poolInfo, poolId, tokenAddress } of poolsWithTokenInfo) {
        let symbol = '';
        let name = '';
        let decimals = 0;

        if (tokenAddress === NativeAddress) {
          // Native token 处理
          symbol = chain.nativeCurrency.symbol;
          name = chain.nativeCurrency.name;
          decimals = chain.nativeCurrency.decimals;
        } else {
          // ERC20 token 处理
          const symbolResult = tokenInfoResults[tokenInfoIndex];
          const nameResult = tokenInfoResults[tokenInfoIndex + 1];
          const decimalsResult = tokenInfoResults[tokenInfoIndex + 2];

          if (symbolResult?.status === 'success') {
            symbol = String(symbolResult.result);
          }
          if (nameResult?.status === 'success') {
            name = String(nameResult.result);
          }
          if (decimalsResult?.status === 'success') {
            decimals = Number(decimalsResult.result);
          }

          tokenInfoIndex += 3;
        }

        allPools.push({ ...poolInfo, poolId, chain, symbol, name, decimals });
      }
    } catch (error) {
      console.error(`Failed to get pools data for chain ${chain.id}:`, error);
    }
  }

  return allPools;
};

// 获取所有 pools 数据的 hook
export const usePoolsData = () => {
  return useQuery({
    queryKey: ['poolsData'],
    queryFn: getAllPoolsData,
    refetchInterval: 600000, // 600 秒刷新一次
  });
};

// 获取所有pools信息和TVL数据
export const useTVLData = () => {
  return useQuery({
    queryKey: ['tvlData'],
    queryFn: async () => {
      const poolsData = await getAllPoolsData();
      const allTokens: TokenInfo[] = [];
      let totalPools = poolsData.length;

      // 按链分组处理 token 数据，利用已获取的 token 信息
      const chainTokenMaps = new Map<SupportedChainId, Map<string, TokenInfo>>();

      for (const pool of poolsData) {
        if (!chainTokenMaps.has(pool.chain.id)) {
          chainTokenMaps.set(pool.chain.id, new Map());
        }

        const tokenMap = chainTokenMaps.get(pool.chain.id)!;
        const tokenAddress = pool.token;
        const existing = tokenMap.get(tokenAddress);

        if (existing) {
          existing.accumulatedFee += pool.accumulatedFee;
        } else {
          // 直接使用 pool 中已获取的 token 信息
          tokenMap.set(tokenAddress, {
            ...pool,
            accumulatedFee: pool.accumulatedFee,
            contractBalance: 0n, // 初始化为0，稍后获取余额
          });
        }
      }

      // 为每个链获取 token 余额（只需要获取余额，token 信息已经有了）
      for (const [chainId, tokenMap] of chainTokenMaps) {
        const contractAddress = CONTRACT_ADDRESSES[chainId];
        if (!contractAddress) continue;
        const client = getPublicClient(chainId);

        const allTokensFromMap = Array.from(tokenMap.values());

        // 准备获取余额的请求
        const balanceContracts = allTokensFromMap
          .map((token) => {
            if (token.token === NativeAddress) {
              return {
                address: token.chain.contracts.multicall3.address,
                abi: multicall3Abi,
                functionName: 'getEthBalance',
                args: [contractAddress],
              } as const;
            }
            return {
              address: token.token,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [contractAddress],
            } as const;
          })
          .filter((contract): contract is NonNullable<typeof contract> => !!contract);

        const balanceResults = balanceContracts.length > 0 ? await client.multicall({ contracts: balanceContracts, allowFailure: true }) : [];

        let balanceIndex = 0;
        for (const token of allTokensFromMap) {
          const balanceResult = balanceResults[balanceIndex];
          if (balanceResult && balanceResult.status === 'success') {
            token.contractBalance = balanceResult.result;
          }
          balanceIndex++;
        }

        allTokens.push(...allTokensFromMap);
      }

      return {
        tokens: allTokens,
        totalPools,
        pools: poolsData, // 也返回 pools 数据，方便其他地方使用
      };
    },
    refetchInterval: 60000, // 60秒刷新一次
  });
};
